import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { PromptInput } from '../components/chat/PromptInput'
import { ConceptPicker, type ConceptAction } from '../components/chat/ConceptPicker'
import {
  SocialTemplatePicker,
  type SocialTemplateAction,
} from '../components/chat/SocialTemplatePicker'
import { CampaignFormModal } from '../components/CampaignFormModal'
import { StrategyProgress } from '../components/StrategyProgress'
import { Header } from '../components/shared/Header'
import { ChatMessage, Conversation, ConversationResponseState } from '../types'
import { BrandKit } from '../types/brandKit'
import {
  FileText,
  Palette,
  Trash2,
  ClipboardList,
  ShoppingBag,
  BarChart3,
  Wand2,
  ArrowRight, ChevronDown, BarChart2, Plus, FolderOpen,
} from 'lucide-react'
import { conversationApi } from '../lib/api/conversations'
import { ConversationItem } from '../components/conversations/ConversationItem'
import { EditConversationModal } from '../components/conversations/EditConversationModal'
import { CampaignList } from './campaigns/CampaignList'
import { BrandKitList } from './brandkits/BrandKitList'
import { ReportsPage } from './reports/ReportsPage'
import { SavedReportsListPage } from './reports/SavedReportsListPage'
import { SelectAdvertiserModal } from '../components/advertisers/SelectAdvertiserModal'
import { Advertiser } from '../lib/api/advertisers'
import { useRole } from '../hooks/useRole'
import { useChat } from '../hooks/useChat'
import { 
  ConversationUpdateData,
  ChatHistoryItem,
  isUserMessage,
  isDisplayableMessage, 
  isCampaignAgentMessage,
  formatCampaignAgentMessage,
  hasAnyResults,
  pickResultTarget,
  hasProposalData,
  isWorkflowComplete,
  shouldShowThinking
} from '../lib/actionCable'

export default function Home() {
  const navigate = useNavigate()
  const { conversationId } = useParams<{ conversationId: string }>()
  const location = useLocation()
  
  // Route detection
  const isCampaignsRoute = location.pathname === '/campaigns'
  const isBrandKitsRoute = location.pathname === '/brand-kits'
  const isReportsRoute = location.pathname.startsWith('/reports')
  const isReportsLoadRoute = location.pathname === '/reports/load'
  const isNewChatRequest = new URLSearchParams(location.search).get('new') === 'true'
  
  // Chat state
  const [isGenerating, setIsGenerating] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentFormData, setCurrentFormData] = useState<any>(null)
  const [strategyProgress, setStrategyProgress] = useState<string[]>([])
  const [responseState, setResponseState] = useState<ConversationResponseState>({})
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('')
  /**
   * When the user clicks "Refine" on a concept card we attach a
   * non-editable chip above the composer (e.g. "Refine 'Card' (c3)")
   * and let the user just type their tweak underneath. On submit we
   * compose the outbound query as `<chip label>: <typed text>` — the
   * chip label itself is never editable text so it can't get partially
   * deleted, mangled, or lost across keystrokes.
   *
   * `id` is stable per card click so PromptInput's re-focus effect
   * retriggers even when the user clicks Refine on the same card
   * twice (label alone would be identical).
   */
  const [pendingRefine, setPendingRefine] = useState<{
    conceptId: string
    conceptTitle?: string
    label: string
    id: number
  } | null>(null)
  /** Debounces concept action buttons — the BE will refresh the picker
   *  phase via cable, so during that in-flight window we want every
   *  card button disabled to prevent double-fires. */
  const [isConceptActionBusy, setIsConceptActionBusy] = useState(false)
  /**
   * Live `template_concepts` object. Derived from `responseState` so
   * every cable update flows through automatically. Passed into every
   * inline `ConceptPicker` so cards resolve to fresh status on refine
   * / finalize transitions.
   */
  const templateConcepts = (responseState as any)?.template_concepts as
    | import('../types').TemplateConceptsState
    | undefined
  /**
   * Social-templates sibling: pending-refine target + busy debounce +
   * derived live picker state. All mirror the `template_concepts`
   * plumbing above so the two flows behave identically — see
   * `handleSocialAction` and the phase-lift effect below for the
   * matching dispatch and cable-driven unlock.
   */
  const [pendingRefineSocial, setPendingRefineSocial] = useState<{
    templateId: string
    templateTitle?: string
    label: string
    id: number
  } | null>(null)
  const [isSocialActionBusy, setIsSocialActionBusy] = useState(false)
  const socialTemplates = (responseState as any)?.social_media_recommended_template as
    | import('../types').SocialMediaRecommendedTemplateState
    | undefined
  const [_currentChatInput, setCurrentChatInput] = useState('')
  const [pendingMessage, setPendingMessage] = useState<string>('')
  const [pendingWorkflow, setPendingWorkflow] = useState<string | null>(null)
  // Attachments staged before the advertiser modal interrupted the flow.
  // Replayed once the user picks an advertiser.
  const [pendingAttachments, setPendingAttachments] = useState<File[] | null>(null)

  // Conversations state
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState(false)
  const [conversationsPagination, setConversationsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false
  })
  
  // Conversation action modals state
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null)
  const [conversationToEdit, setConversationToEdit] = useState<Conversation | null>(null)
  const [isDeletingConversation, setIsDeletingConversation] = useState(false)
  const [isEditingConversation, setIsEditingConversation] = useState(false)
  
  // Advertiser selection state
  const [isAdvertiserModalOpen, setIsAdvertiserModalOpen] = useState(false)
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null)

  // Reports section expand state
  const [isReportsExpanded, setIsReportsExpanded] = useState(isReportsRoute)

  // Brand kit selection state
  const [selectedBrandKit, setSelectedBrandKit] = useState<BrandKit | null>(null)
  const { user, isAdmin } = useRole()
  
  // Refs
  const conversationsListRef = useRef<HTMLDivElement>(null)
  
  // Track if initial conversations load has happened (prevents double API call in StrictMode)
  const hasLoadedConversationsRef = useRef(false)

  // Unified chat hook — manages WS subscription, messages, and send
  const parsedConvId = conversationId ? parseInt(conversationId, 10) : null
  // Mirror parsedConvId into a ref so `handleRawUpdate` (a stable
  // useCallback captured by useChat's subscription effect) can compare
  // the incoming WS frame's conversation id against the currently
  // active URL without re-subscribing. Without this, a late frame for
  // conversation A that arrives after the user switches to B would
  // happily overwrite `currentConversation` / `responseState` with A's
  // data, contributing to the "old chat under new URL" symptom.
  const parsedConvIdRef = useRef<number | null>(parsedConvId)
  useEffect(() => { parsedConvIdRef.current = parsedConvId }, [parsedConvId])

  const handleRawUpdate = useCallback((data: ConversationUpdateData) => {
    const d = (data as any).data ?? data
    // Drop frames that don't belong to the conversation currently in
    // view. The BE broadcasts on per-conversation channels and we
    // unsubscribe on switch, but an in-flight `received()` callback
    // can still fire between "user navigated" and "cleanup ran".
    const incomingId = typeof d?.id === 'number' ? d.id : null
    const activeId = parsedConvIdRef.current
    if (incomingId != null && activeId != null && incomingId !== activeId) {
      // Still let the sidebar reflect background progress — just don't
      // mutate the in-view conversation / responseState / loading flag.
      setConversations(prev => {
        const exists = prev.find(c => c.id === incomingId)
        if (exists) return prev.map(c => c.id === incomingId ? { ...c, ...d } as Conversation : c)
        return prev
      })
      return
    }

    setCurrentConversation(prev => prev ? { ...prev, ...d } : null)
    setResponseState(d.response_state || {})

    // Update loading via shouldShowThinking
    const showThinking = shouldShowThinking(d.response_state, d.chat_history, d.status)
    setIsChatLoading(showThinking)

    // Update conversations list
    setConversations(prev => {
      const exists = prev.find(c => c.id === d.id)
      if (exists) return prev.map(c => c.id === d.id ? { ...c, ...d } as Conversation : c)
      return prev
    })
  }, [])

  const {
    chatMessages,
    setChatMessages,
    isChatLoading,
    setIsChatLoading,
    sendMessage: chatSendMessage,
  } = useChat({
    advertiserId: selectedAdvertiser?.id ?? null,
    conversationId: parsedConvId,
    onConversationCreated: (id, conversation) => {
      setConversations(prev => [conversation, ...prev])
      navigate(`/c/${id}`)
    },
    onRawUpdate: handleRawUpdate,
  })

  // Derive display messages with showResultsButton based on response_state.
  // The latest AI message also carries a deep-link hash so the "Go to ..."
  // button lands the user directly on the most relevant Campaign-page tab
  // (Media Buy when agentic_buy products exist, otherwise Campaign Proposal).
  const displayMessages = useMemo(() => {
    // The "Go to Campaign" button needs two independent things to be true:
    //   1. `response_state` contains *actual* result data for one of the
    //      RESULT_KEYS (see hasAnyResults / resultKeyHasData). This guards
    //      against the workflow streaming scaffolding objects during the
    //      `requirements` phase.
    //   2. The backend has persisted a Campaign row and exposed its id on
    //      the conversation. Without it, handleViewResults can't navigate
    //      anywhere and would only show a "No campaign found" toast.
    //
    // We AND both checks here so the button never appears in a state where
    // clicking it wouldn't actually take the user to a real campaign.
    const conversationCampaignId =
      currentConversation?.campaign?.campaign_id || currentConversation?.campaign_id
    const hasResults = hasAnyResults(responseState as any)
    const target = hasResults && conversationCampaignId
      ? pickResultTarget(responseState as any)
      : null

    // Reference assets that the user uploaded with their request. The BE
    // persists them on response_state so we can render thumbnails even
    // after a refresh. We anchor them to the FIRST user message in the
    // thread (Gemini / ChatGPT-style: attachments sit at the top of the
    // conversation, not floating with the latest reply).
    //
    // All three buckets (image/video/audio) share the same shape —
    // `{ label: url }` — so a single `toUrlList` helper covers them.
    // Non-string entries (e.g. `[]` when the BE returns an empty array
    // instead of an object) are dropped defensively.
    const refData = (responseState as any)?.input_reference_data
    const toUrlList = (bucket: unknown): string[] => {
      if (!bucket || typeof bucket !== 'object') return []
      return Object.values(bucket as Record<string, unknown>).filter(
        (v): v is string => typeof v === 'string' && v.length > 0,
      )
    }
    const referenceImageUrls = toUrlList(refData?.input_image_data)
    const referenceVideoUrls = toUrlList(refData?.input_video_data)
    const referenceAudioUrls = toUrlList(refData?.input_audio_data)

    // Find the indexes we need exactly once; avoids O(n) scans inside the map.
    let firstUserIdx = -1
    let lastAiIdx = -1
    for (let i = 0; i < chatMessages.length; i += 1) {
      if (firstUserIdx === -1 && chatMessages[i].type === 'user') {
        firstUserIdx = i
        break
      }
    }
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      if (chatMessages[i].type === 'ai') {
        lastAiIdx = i
        break
      }
    }

    return chatMessages.map((msg, i) => {
      let next = msg
      if (target && i === lastAiIdx) {
        next = {
          ...next,
          showResultsButton: true,
          resultType: target.label,
          resultTabHash: target.hash,
          resultAdUnitId: target.adUnitId,
        }
      }
      if (
        i === firstUserIdx &&
        (referenceImageUrls.length > 0 ||
          referenceVideoUrls.length > 0 ||
          referenceAudioUrls.length > 0)
      ) {
        next = {
          ...next,
          referenceImages: referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
          referenceVideos: referenceVideoUrls.length > 0 ? referenceVideoUrls : undefined,
          referenceAudio: referenceAudioUrls.length > 0 ? referenceAudioUrls : undefined,
        }
      }
      return next
    })
  }, [chatMessages, responseState, currentConversation])

  // Format AI message for display with headers and bullet points
  // Helper function to render inline bold text (**text**)
  const renderInlineBold = (text: string, keyPrefix: string = ''): React.ReactNode => {
    const boldPattern = /\*\*([^*]+)\*\*/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match
    let keyIndex = 0
    
    while ((match = boldPattern.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index))
      }
      
      // Add the bold text
      parts.push(
        <strong key={`${keyPrefix}bold-${keyIndex++}`} className="font-semibold">
          {match[1]}
        </strong>
      )
      
      lastIndex = match.index + match[0].length
    }
    
    // Add remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }
    
    // If no formatting was found, return original text
    if (parts.length === 0) {
      return text
    }
    
    return <>{parts}</>
  }

  const formatAiMessage = (content: string) => {
    // Split content by double newlines to get sections
    const sections = content.split(/\n\n+/)
    const hasAdditionalContent = sections.length > 1
    
    return sections.map((section, sectionIndex) => {
      const isFirstSection = sectionIndex === 0
      const isLastSection = sectionIndex === sections.length - 1
      
      // Check if section starts with a header (e.g., **Overview**)
      const headerMatch = section.match(/^\*\*(.+?)\*\*\n?(.*)$/s)
      
      if (headerMatch) {
        const headerText = headerMatch[1]
        const bodyText = headerMatch[2]
        
        // Check if body has bullet points
        const lines = bodyText.split('\n').filter(line => line.trim())
        const hasBullets = lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-'))
        
        return (
          <div key={sectionIndex}>
            <div className="py-3">
              <h4 className="font-semibold text-base mb-2 text-primary-600 dark:text-primary-400">{headerText}</h4>
              {hasBullets ? (
                <ul className="space-y-2 ml-1">
                  {lines.map((line, lineIndex) => (
                    <li key={lineIndex} className="text-sm leading-relaxed">
                      {renderInlineBold(line.trim().replace(/^[•-]\s*/, ''), `${sectionIndex}-${lineIndex}-`)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {renderInlineBold(bodyText.trim(), `${sectionIndex}-body-`)}
                </p>
              )}
            </div>
            {!isLastSection && (
              <hr className="border-gray-300 dark:border-gray-600 my-1" />
            )}
          </div>
        )
      }
      
      // Check if section has bullet points without header
      const lines = section.split('\n').filter(line => line.trim())
      const hasBullets = lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-'))
      
      if (hasBullets) {
        return (
          <div key={sectionIndex}>
            <ul className="space-y-2 ml-1 py-3">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex} className="text-sm leading-relaxed">
                  {renderInlineBold(line.trim().replace(/^[•-]\s*/, ''), `${sectionIndex}-${lineIndex}-`)}
                </li>
              ))}
            </ul>
            {!isLastSection && (
              <hr className="border-gray-300 dark:border-gray-600 my-1" />
            )}
          </div>
        )
      }
      
      // Regular paragraph - first section (reply_to_human) gets special styling if there's more content
      const isReplyToHuman = isFirstSection && hasAdditionalContent
      
      return (
        <div key={sectionIndex}>
          <p className={`whitespace-pre-wrap leading-relaxed py-2 ${
            isReplyToHuman 
              ? 'text-base font-medium' 
              : 'text-sm'
          }`}>
            {renderInlineBold(section.trim(), `${sectionIndex}-para-`)}
          </p>
          {!isLastSection && (
            <hr className="border-gray-300 dark:border-gray-600 my-1" />
          )}
        </div>
      )
    })
  }

  // Prompt suggestions - empty array, can be populated from API if needed
  const promptSuggestions: string[] = []

  // Rich starter prompts shown on the welcome screen. Clicking a card fills the
  // textarea (via suggestionText -> PromptInput) so the user can tweak it
  // before submitting. Mirrors the in-campaign ChatPanel for consistency.
  const starterPrompts: Array<{
    icon: React.ReactNode
    title: string
    prompt: string
    accent: string
  }> = [
    {
      icon: <ClipboardList className="h-5 w-5" />,
      title: 'Start a campaign proposal',
      prompt:
        'Help me build a campaign proposal — outline objectives, audience, channels and budget for this advertiser.',
      accent: 'from-primary-500/10 to-blue-500/10 text-primary-600 dark:text-primary-400',
    },
    {
      icon: <ShoppingBag className="h-5 w-5" />,
      title: 'Discover agentic buy inventory',
      prompt:
        'Discover available agentic buy inventory and recommend products that fit my campaign objectives.',
      accent: 'from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'Create a media plan',
      prompt:
        "I'd like to create a media plan — suggest channels, placements and budget allocation for this campaign.",
      accent: 'from-emerald-500/10 to-teal-500/10 text-emerald-600 dark:text-emerald-400',
    },
    {
      icon: <Wand2 className="h-5 w-5" />,
      title: 'Generate AI creative',
      prompt:
        'Create AI-generated ad creatives for this campaign — propose concepts, sizes and copy variations.',
      accent: 'from-fuchsia-500/10 to-pink-500/10 text-fuchsia-600 dark:text-fuchsia-400',
    },
  ]

  // Load conversations list on mount
  useEffect(() => {
    // Prevent double API call in React StrictMode (development)
    if (hasLoadedConversationsRef.current) return
    hasLoadedConversationsRef.current = true
    
    const loadConversations = async () => {
      setIsLoadingConversations(true)
      try {
        const { conversations: data, meta } = await conversationApi.getConversations({ page: 1 })
        setConversations(data)
        setConversationsPagination({
          currentPage: meta.current_page,
          totalPages: meta.total_pages,
          totalCount: meta.total_count,
          hasMore: meta.current_page < meta.total_pages
        })
      } catch (error) {
        console.error('Failed to load conversations:', error)
        // Don't show toast for initial load failure - might be expected
      } finally {
        setIsLoadingConversations(false)
      }
    }
    loadConversations()
  }, [])

  // Load more conversations (infinite scroll)
  const loadMoreConversations = async () => {
    if (isLoadingMoreConversations || !conversationsPagination.hasMore) return
    
    setIsLoadingMoreConversations(true)
    try {
      const nextPage = conversationsPagination.currentPage + 1
      const { conversations: data, meta } = await conversationApi.getConversations({ page: nextPage })
      
      setConversations(prev => [...prev, ...data])
      setConversationsPagination({
        currentPage: meta.current_page,
        totalPages: meta.total_pages,
        totalCount: meta.total_count,
        hasMore: meta.current_page < meta.total_pages
      })
    } catch (error) {
      console.error('Failed to load more conversations:', error)
      toast.error('Failed to load more conversations')
    } finally {
      setIsLoadingMoreConversations(false)
    }
  }

  // Handle scroll for infinite loading
  const handleConversationsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const { scrollTop, scrollHeight, clientHeight } = target
    
    // Load more when scrolled to bottom (with 50px threshold)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreConversations()
    }
  }

  // Conversation action handlers
  const handleShareConversation = (conversation: Conversation) => {
    // Copy conversation URL to clipboard
    const url = `${window.location.origin}/c/${conversation.id}`
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'))
  }

  const handleEditConversationClick = (conversation: Conversation) => {
    setConversationToEdit(conversation)
  }

  const handleDeleteConversation = (conversation: Conversation) => {
    setConversationToDelete(conversation)
  }

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return
    
    setIsDeletingConversation(true)
    try {
      await conversationApi.deleteConversation(conversationToDelete.id)
      setConversations(prev => prev.filter(c => c.id !== conversationToDelete.id))
      toast.success('Conversation deleted')
      
      // If we deleted the current conversation, navigate to home
      if (conversationId && parseInt(conversationId) === conversationToDelete.id) {
        navigate('/')
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      toast.error('Failed to delete conversation')
    } finally {
      setIsDeletingConversation(false)
      setConversationToDelete(null)
    }
  }

  const handleEditConversation = async (data: { query: string; advertiser_id?: number }) => {
    if (!conversationToEdit) return
    
    setIsEditingConversation(true)
    try {
      const updated = await conversationApi.updateConversation(conversationToEdit.id, data)
      setConversations(prev => prev.map(c => 
        c.id === conversationToEdit.id ? { ...c, ...updated } : c
      ))
      toast.success('Conversation updated')
      setConversationToEdit(null)
    } catch (error) {
      console.error('Failed to update conversation:', error)
      toast.error('Failed to update conversation')
      throw error
    } finally {
      setIsEditingConversation(false)
    }
  }

  // Handle advertiser selection and start new chat
  const handleAdvertiserSelected = (advertiser: Advertiser) => {
    setSelectedAdvertiser(advertiser)
    setIsAdvertiserModalOpen(false)
    
    const messageToSend = pendingMessage
    const workflowToSend = pendingWorkflow
    const attachmentsToSend = pendingAttachments

    setChatMessages([])
    setResponseState({})
    setCurrentConversation(null)
    
    if (messageToSend) {
      setPendingMessage('')
      setPendingWorkflow(null)
      setPendingAttachments(null)
      setTimeout(() => {
        handleChatMessage(
          messageToSend,
          advertiser,
          workflowToSend ?? undefined,
          attachmentsToSend ?? undefined,
        )
      }, 100)
    } else {
      setCurrentChatInput('')
    }
  }

  // Handle "new chat" request from URL parameter - just clean up the URL
  useEffect(() => {
    if (isNewChatRequest) {
      navigate('/', { replace: true })
    }
  }, [isNewChatRequest, navigate])

  /**
   * Concept picker: whenever the BE emits a fresh `template_concepts`
   * (phase change, refined child appended, finalized) via cable, lift
   * the debounce flag so the picker becomes interactive again. This is
   * the fast-path — the `setTimeout` fallback in `handleConceptAction`
   * only kicks in if this signal never arrives. Keyed on the whole
   * object identity (not just phase) so refined children arriving
   * mid-`awaiting_selection` also count as "BE has responded".
   */
  useEffect(() => {
    if (templateConcepts) setIsConceptActionBusy(false)
  }, [templateConcepts])

  /**
   * Sibling phase-lift for the social-templates picker. Same rationale
   * as the concept picker above — any fresh
   * `social_media_recommended_template` object arriving over cable
   * (phase change, refined child appended, finalized) counts as "BE
   * has responded", so we drop the busy debounce.
   */
  useEffect(() => {
    if (socialTemplates) setIsSocialActionBusy(false)
  }, [socialTemplates])

  // Track previous path to detect navigation changes
  const prevPathRef = useRef<string>(location.pathname)
  
  // Reset state when navigating to new chat route (/)
  useEffect(() => {
    const currentPath = location.pathname
    const prevPath = prevPathRef.current
    
    const isNavigatingToNewChat = currentPath === '/' && prevPath !== '/'
    
    if (isNavigatingToNewChat) {
      setChatMessages([])
      setCurrentChatInput('')
      setResponseState({})
      setCurrentConversation(null)
      setSelectedAdvertiser(null)
      setSelectedBrandKit(null)
    }
    
    prevPathRef.current = currentPath
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load specific conversation when conversationId changes (WS subscription handled by useChat)
  //
  // Tracks the previous conversationId across effect runs so we can
  // tell "user switched conversations" (must invalidate stale state
  // to kill the old-chat-under-new-URL flash) apart from "we just
  // created this conversation and are navigating into it" (the buffer
  // is the freshly-sent optimistic user message; wiping here would
  // make the first message flicker/vanish until the WS/REST payload
  // arrives).
  const prevLoadedConvIdRef = useRef<string | null>(null)
  useEffect(() => {
    const prevId = prevLoadedConvIdRef.current
    prevLoadedConvIdRef.current = conversationId ?? null

    if (!conversationId) {
      // id→null (back to landing) is always a genuine switch: clear
      // everything so the composer starts fresh.
      setCurrentConversation(null)
      setChatMessages([])
      setResponseState({})
      setIsChatLoading(false)
      setIsLoadingConversation(false)
      return
    }

    const id = parseInt(conversationId, 10)
    if (isNaN(id)) {
      console.error('Invalid conversation ID:', conversationId)
      navigate('/')
      return
    }

    // Only wipe per-conversation state when we're actually switching
    // *between* two different conversations. On the null→id
    // transition (a brand-new conversation the user just created),
    // useChat's `sendMessage` has already appended the user's first
    // message optimistically — we let that survive here and let the
    // REST/WS payload merge in when it arrives.
    const isSwitch = prevId != null && prevId !== conversationId
    if (isSwitch) {
      setCurrentConversation(null)
      setChatMessages([])
      setResponseState({})
      setIsChatLoading(false)
    }
    setIsLoadingConversation(true)

    // Stale-request guard. If the user rapid-fires clicks A→B→C, the B
    // fetch may resolve after C — without this guard, B's payload
    // would happily overwrite C's on the finally block. Every setter
    // below is gated on `!cancelled`, and the effect's cleanup flips
    // the flag on unmount / next switch.
    let cancelled = false

    if (isCampaignsRoute) {
      // On the /campaigns route the Home component is still mounted
      // but the chat isn't in view; skip the fetch entirely and just
      // release the loading flag so it doesn't stay stuck if the user
      // navigates back.
      setIsLoadingConversation(false)
      return () => { cancelled = true }
    }

    const loadConversation = async () => {
      try {
        const conversation = await conversationApi.getConversation(id)
        if (cancelled) return
        setCurrentConversation(conversation)

        if (conversation.chat_history && conversation.chat_history.length > 0) {
          const relevantMessages = conversation.chat_history.filter((msg) =>
            isDisplayableMessage(msg as ChatHistoryItem)
          )

          const messages: ChatMessage[] = relevantMessages.map((msg, index) => {
            let content = msg.message || msg.content || ''
            if (isCampaignAgentMessage(msg as ChatHistoryItem)) {
              content = formatCampaignAgentMessage(content)
            }
            // Mirror the WS mapper: hoist `ref.concept_ids` onto the
            // ChatMessage when the turn is a concepts turn so historical
            // reloads render the picker grid identically to live
            // updates. See `useChat.ts` for the WS-path equivalent.
            const anyMsg = msg as ChatHistoryItem
            const conceptIds =
              anyMsg?.type === 'concepts' && Array.isArray(anyMsg?.ref?.concept_ids)
                ? (anyMsg.ref!.concept_ids as unknown[]).filter(
                    (id): id is string => typeof id === 'string',
                  )
                : undefined
            // Sibling hoist for the social-templates picker; kept in
            // sync with the WS mapper in `useChat.ts` so a REST reload
            // renders the same inline picker as live updates would.
            const socialTemplateIds =
              anyMsg?.type === 'social_templates' &&
              Array.isArray(anyMsg?.ref?.social_template_ids)
                ? (anyMsg.ref!.social_template_ids as unknown[]).filter(
                    (id): id is string => typeof id === 'string',
                  )
                : undefined
            return {
              id: `msg-${id}-${index}`,
              type: isUserMessage(msg as ChatHistoryItem) ? 'user' as const : 'ai' as const,
              content,
              timestamp: new Date(conversation.updated_at || conversation.created_at),
              ...(conceptIds && conceptIds.length > 0 ? { conceptIds } : {}),
              ...(socialTemplateIds && socialTemplateIds.length > 0
                ? { socialTemplateIds }
                : {}),
            }
          })
          if (cancelled) return
          setChatMessages(messages)
        }
        // Note: no `else setChatMessages([])` — we already cleared
        // messages synchronously at the top of the effect. Previously
        // this branch skipped the clear when the conversation was in
        // `pending`/`in_progress`, which was exactly why old messages
        // stuck around on a freshly-created conversation. Sync clear
        // makes the "in_progress skip" both unnecessary and unsafe.

        if (cancelled) return
        setResponseState(conversation.response_state || {})

        const status = typeof conversation.status === 'string' ? conversation.status : ''
        setIsChatLoading(shouldShowThinking(
          conversation.response_state as any,
          conversation.chat_history as any,
          status
        ))
      } catch (error) {
        if (cancelled) return
        console.error('Failed to load conversation:', error)
        toast.error('Failed to load conversation')
        navigate('/')
      } finally {
        if (!cancelled) setIsLoadingConversation(false)
      }
    }

    loadConversation()

    return () => { cancelled = true }
  }, [conversationId, navigate, isCampaignsRoute]) // eslint-disable-line react-hooks/exhaustive-deps

  // Generate strategy progress steps
  const generateStrategyProgress = () => {
    const steps = [
      "Analyzing campaign requirements...",
      "Generating AI-powered ad copies...",
      "Creating creative variants...",
      "Building media plan...",
      "Optimizing audience strategy...",
      "Selecting templates...",
      "Calculating KPIs...",
      "Finalizing strategy..."
    ]
    return steps
  }

  const handleGenerateStrategy = async (formData: any) => {
    setIsGenerating(true)
    setCurrentFormData(formData)
    
    // Start progress animation
    const progressSteps = generateStrategyProgress()
    setStrategyProgress([progressSteps[0]])
    
    // Use the user's prompt directly (from objective field)
    const query = formData.objective || formData.query || 'Create a new campaign'
    
    // Progress animation
    let currentStep = 1
    const progressInterval = setInterval(() => {
      setStrategyProgress(prev => {
        if (currentStep < progressSteps.length) {
          return [...prev, progressSteps[currentStep]]
        }
        return prev
      })
      currentStep++
    }, 800)
    
    try {
      // Check if advertiser is selected
      if (!selectedAdvertiser) {
        clearInterval(progressInterval)
        setIsGenerating(false)
        setPendingMessage(query)
        setIsAdvertiserModalOpen(true)
        return
      }
      
      const payload: { advertiser_id: number; query: string; brand_kit_id?: number } = {
        advertiser_id: selectedAdvertiser.id,
        query: query,
      }
      if (selectedBrandKit) {
        payload.brand_kit_id = selectedBrandKit.id
      }
      const conversation = await conversationApi.createConversation(payload)
      
      // Add to sidebar immediately
      setConversations(prev => [conversation as unknown as Conversation, ...prev])
      
      // Clear interval and complete progress
      clearInterval(progressInterval)
      setStrategyProgress(progressSteps)
      await new Promise(resolve => setTimeout(resolve, 500))
      
      navigate(`/c/${conversation.id}`)
      
    } catch (error: any) {
      console.error('Error generating strategy:', error)
      clearInterval(progressInterval)
      toast.error(error?.message || 'Failed to create conversation. Please try again.')
    } finally {
      setIsGenerating(false)
      setStrategyProgress([])
    }
  }

  const handleChatMessage = async (
    message: string,
    advertiserOverride?: Advertiser,
    workflow?: string,
    attachments?: File[],
  ): Promise<void> => {
    const advertiser = advertiserOverride || selectedAdvertiser

    // The advertiser modal is only meaningful when the user is
    // starting a brand-new conversation — an active conversation
    // already has an advertiser attached on the server. Historically
    // this guard checked only the URL-derived `conversationId`, which
    // caused a false-positive prompt when picker actions (Use this /
    // Refine / Generate more) fired before React re-rendered with a
    // freshly-navigated `/c/:id`. We now also treat `currentConversation`
    // as "conversation is live", so mid-conversation picker actions
    // never re-open the advertiser modal.
    const hasActiveConversation = Boolean(conversationId || currentConversation?.id)
    if (!hasActiveConversation && !advertiser) {
      setPendingMessage(message)
      setPendingWorkflow(workflow ?? null)
      setPendingAttachments(attachments && attachments.length > 0 ? attachments : null)
      setIsAdvertiserModalOpen(true)
      return
    }

    try {
      const extra: Record<string, unknown> = {}
      if (selectedBrandKit) extra.brand_kit_id = selectedBrandKit.id
      if (workflow) extra.workflow = workflow

      // When the user has staged reference assets in the prompt, send the
      // request as multipart so the BE receives the files alongside the
      // workflow/query. Mirrors the contract used by AICreativeModal in
      // Campaigns.tsx (`assets[]` catch-all + typed `reference_*[]` keys).
      //
      // Files are bucketed by MIME so the BE can hydrate the response's
      // `input_reference_data.{input_image_data,input_video_data,input_audio_data}`
      // arrays correctly:
      //   • image/*  → conversation[reference_images][]
      //   • video/*  → conversation[reference_videos][]
      //   • audio/*  → conversation[reference_audio][]
      //   • anything else → falls through to `assets[]` only (still uploaded,
      //     just not tagged with a typed bucket — the BE is free to sniff it).
      // Every file is *also* appended to the generic `assets[]` list so the
      // controller's blanket asset handler picks them all up regardless of
      // which typed bucket a given attachment landed in.
      if (attachments && attachments.length > 0) {
        const fd = new FormData()
        if (workflow) fd.append('conversation[workflow]', workflow)
        fd.append('conversation[status]', 'input_received')
        fd.append('conversation[query]', message)
        if (selectedBrandKit) {
          fd.append('conversation[brand_kit_id]', String(selectedBrandKit.id))
        }
        attachments.forEach(file => {
          fd.append('assets[]', file, file.name)
          const type = (file.type || '').toLowerCase()
          if (type.startsWith('image/')) {
            fd.append('conversation[reference_images][]', file, file.name)
          } else if (type.startsWith('video/')) {
            fd.append('conversation[reference_videos][]', file, file.name)
          } else if (type.startsWith('audio/')) {
            fd.append('conversation[reference_audio][]', file, file.name)
          }
        })
        await chatSendMessage(message, extra, fd)
      } else {
        await chatSendMessage(message, Object.keys(extra).length ? extra : undefined)
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send message')
    }
  }

  /**
   * Ref-latched pointer to the latest `handleChatMessage` closure.
   *
   * Why: `handleChatMessage` is a plain (non-memoized) function that
   * closes over a lot of per-render state (`conversationId`,
   * `currentConversation`, `selectedAdvertiser`, `selectedBrandKit`,
   * etc.). The picker action handlers below are memoized with a
   * narrow dep list — if they captured `handleChatMessage` directly,
   * they'd hold on to an old copy across renders where only e.g. the
   * URL/conversation changed, which is exactly how the "advertiser
   * modal appears mid-conversation" race manifested. Routing the call
   * through a ref that's updated on every render gives the handlers
   * stable identity while always invoking the freshest closure.
   */
  const handleChatMessageRef = useRef(handleChatMessage)
  useEffect(() => {
    handleChatMessageRef.current = handleChatMessage
  })

  /**
   * Concept picker action dispatch.
   *
   * All actions except Refine send an immediate chat message using the
   * normal send flow — the BE interprets the natural-language query
   * into a `concept_action` (finalize / more / skip). Refine is the
   * odd one out: it preseeds the composer with an editable template
   * (`Refine 'Card Title' (c2): `) and focuses the input so the user
   * can describe the tweak before hitting send. This avoids a modal
   * without losing the "which concept + what change" context.
   *
   * We debounce with `isConceptActionBusy` for a brief window so a
   * user double-tap can't fire two actions before the BE cable event
   * updates `template_concepts.phase`. Cable's next update naturally
   * re-enables buttons (via the phase transition), but we also clear
   * the flag on a short timeout so the picker never gets stuck busy
   * if the BE drops the update.
   */
  const handleConceptAction = useCallback(async (action: ConceptAction) => {
    if (isConceptActionBusy) return

    // Refine is a "attach context and wait" action — nothing goes over
    // the wire until the user submits from the composer with their
    // refinement text. We stash the target as a chip above the input
    // (via `attachedContext` on PromptInput) so it stays intact no
    // matter what the user types.
    if (action.kind === 'refine') {
      const label = action.conceptTitle
        ? `Refine '${action.conceptTitle}' (${action.conceptId})`
        : `Refine concept ${action.conceptId}`
      setPendingRefine({
        conceptId: action.conceptId,
        conceptTitle: action.conceptTitle,
        label,
        id: Date.now(),
      })
      // Only one refine chip can be attached at a time; drop any
      // stale social-templates chip so the composer's compose logic
      // stays unambiguous.
      setPendingRefineSocial(null)
      return
    }

    // Build the natural-language query the BE will re-interpret into
    // a `concept_action`. Includes concept id where applicable so the
    // BE never has to guess which card the user meant.
    let query: string
    switch (action.kind) {
      case 'finalize':
        query = action.conceptTitle
          ? `Use concept ${action.conceptId} — '${action.conceptTitle}'.`
          : `Use concept ${action.conceptId}.`
        break
      case 'more':
        query = 'Show me more concepts.'
        break
      case 'skip':
        query = 'Skip the concept suggestions and proceed.'
        break
    }

    setIsConceptActionBusy(true)
    try {
      // Call via ref so this handler stays stable-identity across
      // renders while always dispatching to the freshest
      // `handleChatMessage` closure (which captures the current
      // conversation / advertiser / brand-kit state).
      await handleChatMessageRef.current(query, undefined, 'ai_creative_concept')
    } finally {
      // Safety net — cable will normally flip us out of busy via the
      // phase change, but if the BE drops that update we still recover
      // after 8s rather than leaving the picker permanently frozen.
      setTimeout(() => setIsConceptActionBusy(false), 8000)
    }
  }, [isConceptActionBusy])

  /**
   * Social-templates picker action dispatch.
   *
   * • `finalize`      — user picked a card; send a natural-language
   *   message so the BE re-interprets it into a template action.
   * • `generate_more` — user wants new templates via AI. This behaves
   *   like ConceptPicker's Refine: we stage a chip on the composer so
   *   the user can describe what they want (colors, tone, surface, …)
   *   before sending. No message goes out until the user submits from
   *   the composer.
   *
   * There is intentionally no per-card `refine` or grid-level `skip`
   * for this picker — see the SocialTemplateAction docblock.
   */
  const handleSocialAction = useCallback(
    async (action: SocialTemplateAction) => {
      if (isSocialActionBusy) return

      if (action.kind === 'generate_more') {
        setPendingRefineSocial({
          templateId: '',
          templateTitle: undefined,
          label: 'Generate more with AI',
          id: Date.now(),
        })
        // Only one composer chip is active at a time — clear any
        // stale concept-refine target so the submit path stays
        // unambiguous.
        setPendingRefine(null)
        return
      }

      const query = action.templateTitle
        ? `Use template ${action.templateId} — '${action.templateTitle}'.`
        : `Use template ${action.templateId}.`

      setIsSocialActionBusy(true)
      try {
        // See sibling comment in handleConceptAction — the ref
        // indirection keeps this callback stable-identity without
        // capturing a stale handleChatMessage closure.
        await handleChatMessageRef.current(query)
      } finally {
        setTimeout(() => setIsSocialActionBusy(false), 8000)
      }
    },
    [isSocialActionBusy],
  )

  // Handle "View Results" button click - navigate to campaign page.
  // Optional `tabHash` deep-links into a specific Campaign tab; when
  // `adUnitId` is provided we instead route into the Creative Studio for
  // that exact unit (Start-with-AI / ai_creative_generation flow).
  const handleViewResults = (tabHash?: string, adUnitId?: number) => {
    const campaignId = currentConversation?.campaign?.campaign_id || currentConversation?.campaign_id

    if (!campaignId) {
      toast.error('No campaign found for this conversation')
      return
    }

    const navigationState = {
      apiResponse: {
        inputs: responseState
      },
      formData: {
        advertiser_id: currentConversation?.advertiser_id,
        query: currentConversation?.query,
      },
      conversationId: currentConversation?.id,
      campaign: currentConversation?.campaign,
      isStreaming: !isWorkflowComplete(responseState as any),
      from: currentConversation?.id ? `/c/${currentConversation.id}` : undefined,
    }

    if (adUnitId) {
      navigate(`/campaigns/${campaignId}/ad_units/${adUnitId}`, { state: navigationState })
      return
    }

    // Fall back to the page default ("campaignproposal") when no specific
    // hash was provided so the URL still encodes a stable tab anchor.
    const fallback = pickResultTarget(responseState as any).hash
    const hash = tabHash || fallback
    const target = `/campaigns/${campaignId}${hash ? `#${hash}` : ''}`
    navigate(target, { state: navigationState })
  }

  // Auto-scroll to bottom when messages change
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [displayMessages, isChatLoading])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 flex flex-col">
      <Header variant="home" />

      <main className="flex-1 bg-white dark:bg-gray-950 pt-14 sm:pt-16 overflow-hidden transition-colors">
        <div className="h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)] flex relative">
            {/* Left Sidebar - ChatGPT Style */}
            <div className="w-64 border-r border-slate-200/70 dark:border-slate-800/80 bg-white/95 dark:bg-slate-900/80 backdrop-blur flex-shrink-0 flex flex-col absolute left-0 top-0 bottom-0 z-10">
              {/* Sticky Top Section */}
              <div className="p-3 space-y-2 flex-shrink-0">
                {/* New Chat Button */}
                <button 
                  onClick={() => {
                    navigate('/')
                    setChatMessages([])
                    setCurrentChatInput('')
                    setResponseState({})
                    setCurrentConversation(null)
                    setSelectedAdvertiser(null)
                    setSelectedBrandKit(null)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                    !isCampaignsRoute && !isBrandKitsRoute && !conversationId
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>New chat</span>
                </button>

                {/* Campaigns Navigation Option */}
                <button 
                  onClick={() => navigate('/campaigns')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                    isCampaignsRoute
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span>Campaigns</span>
                </button>

                {/* Reports Navigation Option */}
                <div>
                  <button
                    onClick={() => setIsReportsExpanded(v => !v)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                      isReportsRoute && !isReportsExpanded
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <BarChart2 className="w-5 h-5" />
                    <span className="flex-1 text-left">Reports</span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isReportsExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isReportsExpanded && (
                    <div className="mt-0.5 ml-4 space-y-0.5">
                      <button
                        onClick={() => navigate('/reports')}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                          isReportsRoute && !isReportsLoadRoute
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        New Report
                      </button>
                      <button
                        onClick={() => navigate('/reports/load')}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                          isReportsLoadRoute
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        <FolderOpen className="w-4 h-4" />
                        Load Report
                      </button>
                    </div>
                  )}
                </div>

                {/* Brand Kits Navigation Option */}
                <button
                  onClick={() => navigate('/brand-kits')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                    isBrandKitsRoute
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Palette className="w-5 h-5" />
                  <span>Brand Kits</span>
                </button>

                {/* Search Bar */}
                <div className="relative py-1">
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900/70 text-sm text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Recent Label */}
                <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 px-3 pt-2 uppercase tracking-wide">
                  Recent
                </h2>
              </div>

              {/* Scrollable Conversations List */}
              <div 
                ref={conversationsListRef}
                onScroll={handleConversationsScroll}
                className="flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="space-y-1">
                  {isLoadingConversations ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      Loading...
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No conversations yet
                    </div>
                  ) : (
                    <>
                      {conversations.map((conversation) => (
                        <ConversationItem
                          key={conversation.id}
                          conversation={conversation}
                          isActive={conversationId === conversation.id.toString()}
                          onShare={handleShareConversation}
                          onEdit={handleEditConversationClick}
                          onDelete={handleDeleteConversation}
                        />
                      ))}
                      
                      {/* Loading more indicator */}
                      {isLoadingMoreConversations && (
                        <div className="flex items-center justify-center py-3">
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <div className="w-3 h-3 border-2 border-gray-300 dark:border-gray-600 border-t-primary-500 rounded-full animate-spin"></div>
                            <span>Loading...</span>
                          </div>
                        </div>
                      )}
                      
                      {/* End of list indicator */}
                      {!conversationsPagination.hasMore && conversations.length > 0 && !isLoadingMoreConversations && (
                        <div className="text-center py-2">
                          <span className="text-xs text-gray-400 dark:text-gray-600">No more conversations</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col bg-white dark:bg-gray-950 ml-64 h-full transition-colors">
              {/* View Campaign Button - Show if conversation has campaign */}
              {!isCampaignsRoute && !isBrandKitsRoute && !isReportsRoute && (currentConversation?.campaign?.campaign_id || currentConversation?.campaign_id) && (
                <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-3 flex justify-end">
                  <button
                    onClick={() => {
                      const cid = currentConversation?.campaign?.campaign_id || currentConversation?.campaign_id
                      // Land on Campaign Proposal only when this
                      // conversation actually has proposal-shaped data;
                      // otherwise default to Creative Generation, which
                      // is always visible on the Campaigns page. This
                      // avoids landing on an invisible/empty proposal
                      // tab for conversations that never ran a
                      // proposal workflow (e.g. "Start with AI creative").
                      const hash = hasProposalData(responseState as any)
                        ? 'campaignproposal'
                        : 'creativegeneration'
                      navigate(`/campaigns/${cid}#${hash}`, {
                        state: {
                          from: currentConversation?.id ? `/c/${currentConversation.id}` : undefined,
                          // Pre-seed the Campaigns page with the fresh
                          // response_state so its tab-visibility
                          // computation reflects THIS conversation on
                          // first paint, instead of falling back to
                          // any stale REST snapshot.
                          apiResponse: { inputs: responseState },
                          conversationId: currentConversation?.id,
                        },
                      })
                    }}
                    className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    View Campaign
                  </button>
                </div>
              )}
              
              {/* Campaigns List - Show when on /campaigns route */}
              {isCampaignsRoute ? (
                <CampaignList />
              ) : isBrandKitsRoute ? (
                <BrandKitList />
              ) : isReportsLoadRoute ? (
                <SavedReportsListPage />
              ) : isReportsRoute ? (
                <ReportsPage />
              ) : (isLoadingConversation ||
                  (parsedConvId != null && currentConversation?.id !== parsedConvId)) ? (
                // Show the spinner whenever the loaded conversation
                // doesn't match the current URL — either because the
                // load effect is in-flight (`isLoadingConversation`)
                // *or* because the URL just changed and we haven't
                // rendered a state reset yet (catches the
                // between-frames flash where React re-renders with
                // the new `parsedConvId` while `currentConversation`
                // still points at the previous chat).
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading conversation...</p>
                  </div>
                </div>
              ) : displayMessages.length === 0 ? (
                /* Welcome Screen with Form - Show when no messages */
                <div className="flex-1 flex items-center justify-center px-4 overflow-y-auto">
                  <div className="max-w-4xl w-full text-center py-8">
                    {/* Greeting — replaced the typographic "AgenX" with
                        the new wordmark SVG so the splash now mirrors the
                        header branding. Black/white pair toggles via dark:. */}
                    <div className="mb-8 flex flex-col items-center">
                      <img
                        src="/main_logo/agenx_logo_black.svg"
                        alt="AgenX"
                        className="block dark:hidden h-12 sm:h-14 w-auto mb-4"
                      />
                      <img
                        src="/main_logo/agenx_logo_white.svg"
                        alt="AgenX"
                        aria-hidden="true"
                        className="hidden dark:block h-12 sm:h-14 w-auto mb-4"
                      />
                      <p className="text-xl text-gray-600 dark:text-gray-300 font-light">
                        How can I help you create your campaign today?
                      </p>
                    </div>

                    {/* Legacy text suggestions (only shown if API populates them) */}
                    {promptSuggestions.length > 0 && (
                      <div className="mb-8">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Try these examples:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
                          {promptSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedSuggestion(suggestion)}
                              className="text-left px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 hover:border-primary-500 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
                            >
                              <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400">
                                {suggestion}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Form Input Area - Centered */}
                    <div className="w-full">
                      <PromptInput 
                        onSubmit={(formData) => {
                          const message = formData.objective || 'Create a new campaign'
                          handleChatMessage(message, undefined, formData.workflow, formData.attachments)
                          setSelectedSuggestion('')
                        }} 
                        isLoading={isGenerating || isChatLoading}
                        suggestionText={selectedSuggestion}
                        selectedBrandKit={selectedBrandKit}
                        onSelectBrandKit={setSelectedBrandKit}
                        showBrandKitOption
                        showWorkflowOptions
                        brandKitAdvertiserId={
                          isAdmin()
                            ? undefined
                            : selectedAdvertiser
                              ? String(selectedAdvertiser.id)
                              : user?.manageable_id
                                ? String(user.manageable_id)
                                : undefined
                        }
                      />
                    </div>

                    {/* Starter prompt cards — clicking fills the input above */}
                    <div className="mt-8 max-w-3xl mx-auto">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 text-center">
                        Quick starters
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {starterPrompts.map((sp, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedSuggestion(sp.prompt)}
                            className="group text-left rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all duration-200"
                          >
                            <div className="flex items-start gap-3 px-4 py-3.5">
                              <span
                                className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${sp.accent}`}
                              >
                                {sp.icon}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                                  {sp.title}
                                </p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-snug line-clamp-2">
                                  {sp.prompt}
                                </p>
                              </div>
                              <ArrowRight className="flex-shrink-0 h-4 w-4 text-gray-300 dark:text-gray-600 mt-1 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Chat Messages Area */
                <div className="flex-1 overflow-y-auto px-4 py-6">
                  <div className="max-w-3xl mx-auto space-y-6">
                    {displayMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} items-start gap-3`}
                      >
                        {message.type === 'ai' && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center overflow-hidden">
                            <img
                              src="/favicon.png"
                              alt="AgenX"
                              className="h-5 w-5 object-contain"
                              draggable={false}
                            />
                          </div>
                        )}
                        <div className="max-w-[85%] min-w-0 text-left">
                          {/* Reference asset gallery — surfaces every
                              file the user uploaded with this
                              conversation (sourced from
                              response_state.input_reference_data.{input_image_data,
                              input_video_data, input_audio_data} by
                              displayMessages). Pinned to the FIRST user
                              message and rendered ABOVE the bubble, the
                              same way Gemini / ChatGPT show attachments.
                              Each media type gets its own tile shape:
                              images use <img>, videos an inline muted
                              autoplay preview (click through opens the
                              raw URL), audio a compact <audio> control. */}
                          {message.type === 'user' &&
                            ((message.referenceImages && message.referenceImages.length > 0) ||
                              (message.referenceVideos && message.referenceVideos.length > 0) ||
                              (message.referenceAudio && message.referenceAudio.length > 0)) && (
                              <div className="mb-2 flex flex-wrap justify-end gap-1.5">
                                {message.referenceImages?.map((url, idx) => (
                                  <a
                                    key={`${message.id}-refimg-${idx}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-16 h-16 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 shadow-sm hover:shadow-md hover:opacity-95 transition-all"
                                    title="Open reference image in new tab"
                                  >
                                    <img
                                      src={url}
                                      alt={`Reference ${idx + 1}`}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  </a>
                                ))}
                                {message.referenceVideos?.map((url, idx) => (
                                  <a
                                    key={`${message.id}-refvid-${idx}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="relative block w-16 h-16 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-black shadow-sm hover:shadow-md hover:opacity-95 transition-all group"
                                    title="Open reference video in new tab"
                                  >
                                    {/* `preload="metadata"` gets a poster
                                        frame without pulling the whole
                                        clip; muted+playsInline lets us
                                        skip autoplay policy prompts if
                                        we ever want to auto-preview. */}
                                    <video
                                      src={url}
                                      className="w-full h-full object-cover"
                                      preload="metadata"
                                      muted
                                      playsInline
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                                      <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow">
                                        <svg className="w-3 h-3 text-gray-900 translate-x-[1px]" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </div>
                                    </div>
                                  </a>
                                ))}
                                {message.referenceAudio?.map((url, idx) => (
                                  <div
                                    key={`${message.id}-refaud-${idx}`}
                                    className="flex items-center justify-center h-16 px-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 shadow-sm"
                                    title="Reference audio"
                                  >
                                    <audio src={url} controls className="h-8 max-w-[200px]" preload="metadata" />
                                  </div>
                                ))}
                              </div>
                            )}
                          {/* `max-w-full` keeps the bubble inside the
                              parent column even when the message contains
                              long unbreakable tokens (e.g. URLs, query
                              strings). `[overflow-wrap:anywhere]` is the
                              modern equivalent of `word-break: break-word`
                              that actually breaks inside long URLs without
                              the harsh `break-all` look on normal text. */}
                          <div className={`inline-block max-w-full px-4 py-3 rounded-2xl [overflow-wrap:anywhere] ${
                            message.type === 'user'
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          }`}>
                            {message.type === 'ai' ? (
                              <div className="text-sm leading-relaxed space-y-3 [overflow-wrap:anywhere]">
                                {formatAiMessage(message.content)}
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap leading-relaxed [overflow-wrap:anywhere]">
                                {message.content}
                              </p>
                            )}
                          </div>
                          {/* View Results Button - shown when results are ready */}
                          {message.showResultsButton && (
                            <div className="mt-3">
                              <button
                                onClick={() => handleViewResults(message.resultTabHash, message.resultAdUnitId)}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                                Go to {message.resultType || 'Results'}
                              </button>
                            </div>
                          )}
                          {/* Inline concept picker — anchored to each
                              TemplateConceptAgent turn that carries
                              `ref.concept_ids`. Ids resolve against the
                              live `template_concepts.items` so status
                              (refined / finalized) stays fresh on old
                              turns even after subsequent picker updates. */}
                          {message.conceptIds && message.conceptIds.length > 0 && (
                            <ConceptPicker
                              conceptIds={message.conceptIds}
                              state={templateConcepts}
                              onAction={handleConceptAction}
                              isBusy={isConceptActionBusy}
                            />
                          )}
                          {/* Sibling inline picker for the
                              SocialMediaRecommendationAgent turn. Same
                              anchoring model as ConceptPicker — one per
                              turn that carries `ref.social_template_ids`,
                              cards resolve against the live
                              `social_media_recommended_template.items`. */}
                          {message.socialTemplateIds && message.socialTemplateIds.length > 0 && (
                            <SocialTemplatePicker
                              templateIds={message.socialTemplateIds}
                              state={socialTemplates}
                              onAction={handleSocialAction}
                              isBusy={isSocialActionBusy}
                            />
                          )}
                        </div>
                        {message.type === 'user' && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                            <span className="text-white text-xs font-medium">U</span>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {isChatLoading && (
                      <div className="flex justify-start items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center overflow-hidden">
                          <img
                            src="/favicon.png"
                            alt="AgenX"
                            className="h-5 w-5 object-contain"
                            draggable={false}
                          />
                        </div>
                        <div className="max-w-[85%]">
                          <div className="inline-block px-4 py-3 rounded-2xl bg-gray-100 dark:bg-gray-800">
                            <span className="text-sm font-medium text-primary-600 dark:text-primary-400 animate-pulse">
                              Thinking...
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
              )}
              
              {/* Chat Input - Always visible at bottom when in chat mode */}
              {!isCampaignsRoute && displayMessages.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-4 transition-all">
                  <div className="max-w-3xl mx-auto">
                    <PromptInput
                      key={`chat-form-${displayMessages.length}`}
                      onSubmit={(formData: any) => {
                        const typed = formData.objective || 'Continue conversation'
                        // When a Refine chip is attached, compose the
                        // outbound query as `<chip label>: <typed>`.
                        // For the concept picker we also force the
                        // `ai_creative_concept` workflow; the social
                        // template refine reuses the active workflow
                        // (BE re-interprets the message from context).
                        // Only one chip is ever active at a time — the
                        // pickers clear each other on set — so this
                        // priority order is only a tie-breaker.
                        const activeRefine =
                          pendingRefine
                            ? { label: pendingRefine.label, workflow: 'ai_creative_concept' as const }
                            : pendingRefineSocial
                            ? { label: pendingRefineSocial.label, workflow: undefined }
                            : null
                        const message = activeRefine
                          ? `${activeRefine.label}: ${typed}`
                          : typed
                        const workflow = activeRefine?.workflow ?? formData.workflow
                        handleChatMessage(message, undefined, workflow, formData.attachments)
                        if (pendingRefine) setPendingRefine(null)
                        if (pendingRefineSocial) setPendingRefineSocial(null)
                      }}
                      isLoading={isGenerating || isChatLoading}
                      selectedBrandKit={selectedBrandKit}
                      attachedContext={
                        pendingRefine
                          ? {
                              // Bumping the label via the id keeps
                              // PromptInput's re-focus effect firing
                              // when the user clicks Refine on the same
                              // card twice — the id changes even if the
                              // human-readable label is identical.
                              label: pendingRefine.label,
                              onClear: () => setPendingRefine(null),
                            }
                          : pendingRefineSocial
                          ? {
                              label: pendingRefineSocial.label,
                              onClear: () => setPendingRefineSocial(null),
                            }
                          : null
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
      </main>

      {/* Strategy Progress Modal */}
      <StrategyProgress
        progress={strategyProgress}
        isVisible={isGenerating}
      />

      {/* Campaign Form Modal */}
      <CampaignFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleGenerateStrategy}
        isLoading={isGenerating}
        currentData={currentFormData}
      />

      {/* Delete Conversation Confirmation Modal */}
      {conversationToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeletingConversation && setConversationToDelete(null)}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Conversation
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">"{conversationToDelete.query || 'this conversation'}"</span>?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConversationToDelete(null)}
                disabled={isDeletingConversation}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteConversation}
                disabled={isDeletingConversation}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeletingConversation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Conversation Modal */}
      <EditConversationModal
        isOpen={!!conversationToEdit}
        onClose={() => setConversationToEdit(null)}
        onSubmit={handleEditConversation}
        isLoading={isEditingConversation}
        conversation={conversationToEdit}
      />

      {/* Select Advertiser Modal */}
      <SelectAdvertiserModal
        isOpen={isAdvertiserModalOpen}
        onClose={() => {
          setIsAdvertiserModalOpen(false)
          // Clear the pending message if user cancels
          if (!selectedAdvertiser) {
            setCurrentChatInput('')
          }
        }}
        onConfirm={handleAdvertiserSelected}
      />

    </div>
  )
}
